import { useEffect, useRef } from 'react'
import { getWidgetCable, releaseWidgetCable } from '@/lib/widgetCable'
import type { Consumer, Subscription } from '@rails/actioncable'

export interface NewMessageNotification {
  id: number
  body: string
  channel: string
  conversation_id: number
  author_name: string
  author_avatar_url: string | null
}

export interface MessageDeletedNotification {
  message_id: number
  conversation_id: number
}

interface BroadcastData {
  type: 'new_message' | 'message_deleted'
  message?: NewMessageNotification
  message_id?: number
  conversation_id?: number
}

interface UseEndUserChannelProps {
  apiUrl: string
  sessionToken: string
  onNewMessage: (message: NewMessageNotification) => void
  onMessageDeleted?: (data: MessageDeletedNotification) => void
}

export function useEndUserChannel({
  apiUrl,
  sessionToken,
  onNewMessage,
  onMessageDeleted,
}: UseEndUserChannelProps) {
  const cableRef = useRef<Consumer | null>(null)
  const subscriptionRef = useRef<Subscription | null>(null)

  // Use refs for callbacks to avoid recreating subscription
  const onNewMessageRef = useRef(onNewMessage)
  const onMessageDeletedRef = useRef(onMessageDeleted)

  useEffect(() => {
    onNewMessageRef.current = onNewMessage
    onMessageDeletedRef.current = onMessageDeleted
  }, [onNewMessage, onMessageDeleted])

  useEffect(() => {
    if (!sessionToken || !apiUrl) return

    // Get shared cable consumer (session token passed via subscription params, not URL)
    cableRef.current = getWidgetCable(apiUrl)

    // Subscribe to the end user channel with session token for authentication
    const subscription = cableRef.current.subscriptions.create(
      {
        channel: 'EndUserChannel',
        session_token: sessionToken,
      },
      {
        connected() {},
        disconnected() {},
        received(data: BroadcastData) {
          if (data.type === 'new_message' && data.message) {
            onNewMessageRef.current(data.message)
          } else if (data.type === 'message_deleted' && data.message_id && data.conversation_id && onMessageDeletedRef.current) {
            onMessageDeletedRef.current({ message_id: data.message_id, conversation_id: data.conversation_id })
          }
        },
      }
    )

    subscriptionRef.current = subscription

    // Cleanup: unsubscribe when component unmounts
    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
      releaseWidgetCable()
      cableRef.current = null
    }
  }, [apiUrl, sessionToken])
}
